import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { getFieldValue } from "@store/Store/Components/ContactUsForm/ContactUsFormSelectors";
import useFieldValidation from "@store/Store/Components/ContactUsForm/Hooks/useFieldValidation";
import React, { ChangeEvent, FC, FocusEvent, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const fieldKey = "faxNumber";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    value: getFieldValue(state, fieldKey),
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ContactUsFaxNumberStyles {
    faxNumberTextField?: TextFieldPresentationProps;
}

export const contactUsFaxNumberStyles: ContactUsFaxNumberStyles = {};

const styles = contactUsFaxNumberStyles;
const faxRegex = /^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/;

const ContactUsFaxNumber: FC<Props> = ({ fields, value, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const [errorMessage, setErrorMessage] = useState("");

    const validate = () => {
        const message =
            !value && isRequired
                ? siteMessage(siteMessage("Field_Required", label) as string)
                : !value || faxRegex.test(value)
                ? ""
                : siteMessage(siteMessage("Field_Invalid", label) as string);

        setErrorMessage(message as string);
        return !message;
    };

    useFieldValidation(fieldKey, value, validate);

    const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: fieldKey, value: event.target.value });
    };

    const blurHandler = (event: FocusEvent<HTMLInputElement>) => {
        validate();
    };

    return (
        <TextField
            {...styles.faxNumberTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={value || ""}
            onChange={changeHandler}
            onBlur={blurHandler}
            error={errorMessage}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ContactUsFaxNumber),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Fax Number",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "(###) ###-####",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
